'use client';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceSubscriptionsOptimized } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';
import { AlertTriangle, Clock, Crown, Settings, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { FC, useMemo } from 'react';

interface TrialStatusBannerProps {
  className?: string;
  compact?: boolean;
}

const TrialStatusBanner: FC<TrialStatusBannerProps> = ({ className, compact = false }) => {
  const router = useRouter();
  const workspaceContext = useWorkspace();

  const activeWorkspace = workspaceContext?.activeWorkspace;

  const { data: subscriptionsData } = useWorkspaceSubscriptionsOptimized({
    enabled: !!activeWorkspace?.vendorid,
    workspaceId: activeWorkspace?.vendorid,
  });

  const workspaceId = activeWorkspace?.vendorid;

  const bannerState = useMemo(() => {
    if (!subscriptionsData?.subscriptions || !workspaceId) return null;

    // Find the soonest-expiring active trial so we surface the most urgent countdown
    const trialSubscriptions = subscriptionsData.subscriptions.filter((sub) => sub.status === 'trial');
    const trialSubscription = trialSubscriptions
      .filter((sub) => sub.current_period_end)
      .sort((a, b) => new Date(a.current_period_end!).getTime() - new Date(b.current_period_end!).getTime())[0]
      ?? trialSubscriptions[0];

    if (trialSubscription && trialSubscription.current_period_end) {
      const trialEndDate = new Date(trialSubscription.current_period_end);
      const now = new Date();
      const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      let urgencyLevel: 'low' | 'medium' | 'high' = 'low';
      if (daysRemaining <= 3) urgencyLevel = 'high';
      else if (daysRemaining <= 7) urgencyLevel = 'medium';

      const moduleLabel = trialSubscription.product_module === 'marketing' ? 'Marketing' : 'Sales';
      return {
        type: 'active' as const,
        daysRemaining: Math.max(0, daysRemaining),
        urgencyLevel,
        expiryLabel: trialEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        moduleLabel,
      };
    }

    // Check if there's a prior inactive subscription — trial expired, not yet subscribed
    const hasInactiveSub = subscriptionsData.subscriptions.some((sub) => sub.status === 'inactive');
    const hasActiveSub = subscriptionsData.subscriptions.some(
      (sub) => sub.status === 'active',
    );

    if (hasInactiveSub && !hasActiveSub) {
      return { type: 'expired' as const };
    }

    return null;
  }, [subscriptionsData, workspaceId]);

  if (!bannerState || !activeWorkspace) return null;

  // Expired trial banner
  if (bannerState.type === 'expired') {
    if (compact) {
      return (
        <Card className={cn('relative border-0 shadow-sm overflow-hidden', className)}>
          <div className='absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 opacity-10 pointer-events-none' />
          <div className='relative p-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='p-1.5 bg-gradient-to-r from-red-600 to-orange-600 rounded-full'>
                  <AlertTriangle className='h-4 w-4 text-white' />
                </div>
                <div className='flex flex-col'>
                  <span className='text-sm font-semibold text-red-700'>Trial Ended</span>
                  <span className='text-xs text-red-600'>Subscribe to continue</span>
                </div>
              </div>
              <div className='flex items-center gap-2'>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => window.open('/book-demo', '_blank')}
                  className='text-xs border-gray-300 text-gray-600 hover:bg-gray-50'
                >
                  Book a Demo
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => router.push('/settings?tab=billing-and-usage')}
                  className='text-xs border-red-300 text-red-700 hover:bg-red-50'
                >
                  Subscribe Now
                </Button>
              </div>
            </div>
          </div>
        </Card>
      );
    }

    return (
      <Card className={cn('relative overflow-hidden border-0 shadow-lg', className)}>
        <div className='absolute inset-0 bg-gradient-to-r from-red-600 to-orange-600 pointer-events-none' />
        <div className='relative p-6'>
          <div className='flex items-start justify-between mb-4'>
            <div className='flex items-center gap-3'>
              <div className='w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm'>
                <AlertTriangle className='h-6 w-6 text-white' />
              </div>
              <div>
                <h3 className='text-xl font-bold text-white'>Trial Period Ended</h3>
                <p className='text-red-100 text-sm'>
                  Your data and workflows are preserved. Subscribe to continue.
                </p>
              </div>
            </div>
          </div>
          <div className='flex items-center gap-3'>
            <Button
              onClick={() => router.push('/settings?tab=billing-and-usage')}
              className='bg-white text-red-700 hover:bg-red-50 font-semibold shadow-lg'
            >
              <TrendingUp className='h-4 w-4 mr-2' />
              Subscribe Now
            </Button>
            <Button
              variant='outline'
              onClick={() => window.open('/book-demo', '_blank')}
              className='!bg-white/10 border-white/50 text-white hover:!bg-white/20 backdrop-blur-sm'
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Active trial banner
  const getGradientByUrgency = (level: string) => {
    switch (level) {
      case 'high': return 'from-red-600 to-orange-600';
      case 'medium': return 'from-orange-600 to-amber-600';
      default: return 'from-blue-600 to-blue-700';
    }
  };

  const getTextByUrgency = (level: string) => {
    switch (level) {
      case 'high': return 'text-red-700';
      case 'medium': return 'text-orange-700';
      default: return 'text-blue-700';
    }
  };

  const gradient = getGradientByUrgency(bannerState.urgencyLevel);
  const textColor = getTextByUrgency(bannerState.urgencyLevel);

  if (compact) {
    return (
      <Card className={cn('relative border-0 shadow-sm overflow-hidden', className)}>
        <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-10 pointer-events-none`} />
        <div className='relative p-3'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className={`p-1.5 bg-gradient-to-r ${gradient} rounded-full`}>
                <Crown className='h-4 w-4 text-white' />
              </div>
              <div className='flex flex-col'>
                <span className={`text-sm font-semibold ${textColor}`}>
                  {bannerState.moduleLabel} Trial
                </span>
                <span className={`text-xs ${textColor}`}>
                  {bannerState.daysRemaining}d left · {bannerState.moduleLabel === 'Marketing' ? 'ends' : 'auto-charges'} {bannerState.expiryLabel}
                </span>
              </div>
            </div>
            <Button
              size='sm'
              variant='outline'
              onClick={() => router.push('/settings?tab=billing-and-usage')}
              className='text-xs'
            >
              Upgrade
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden border-0 shadow-lg', className)}>
      <div className={`absolute inset-0 bg-gradient-to-r ${gradient} pointer-events-none`} />

      <div className='absolute inset-0 overflow-hidden pointer-events-none'>
        <Sparkles className='absolute top-4 left-6 h-4 w-4 text-blue-200 animate-pulse' />
        <Sparkles className='absolute top-8 right-12 h-3 w-3 text-blue-200 animate-pulse delay-700' />
        <Sparkles className='absolute bottom-6 left-12 h-3 w-3 text-blue-300 animate-pulse delay-1000' />
      </div>

      <div className='relative p-6'>
        <div className='flex items-start justify-between mb-4'>
          <div className='flex items-center gap-3'>
            <div className='relative'>
              <div className='w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm'>
                <Crown className='h-6 w-6 text-white' />
              </div>
              <div className='absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center'>
                <Sparkles className='h-2 w-2 text-amber-800' />
              </div>
            </div>

            <div>
              <h3 className='text-xl font-bold text-white'>
                {bannerState.moduleLabel} Trial Active
              </h3>
              <p className='text-blue-100 text-sm'>
                Workspace: <strong className='text-white'>{activeWorkspace.workspace_name}</strong>
              </p>
            </div>
          </div>

          <div className='text-right'>
            <div className='flex items-center gap-2 text-white/90 text-sm'>
              <Clock className='h-4 w-4' />
              <span className='font-semibold'>{bannerState.daysRemaining}</span>
              <span>days left</span>
            </div>
            <p className='text-white/70 text-xs mt-1'>{bannerState.moduleLabel === 'Marketing' ? 'ends' : 'auto-charges'} {bannerState.expiryLabel}</p>
          </div>
        </div>

        <div className='flex items-center gap-3'>
          <Button
            onClick={() => router.push('/settings?tab=billing-and-usage')}
            className='bg-white text-blue-700 hover:bg-blue-50 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200'
          >
            <TrendingUp className='h-4 w-4 mr-2' />
            Upgrade Now
          </Button>

          <Button
            variant='outline'
            onClick={() => router.push('/settings?tab=workspace')}
            className='!bg-white/10 border-white/50 text-white hover:!bg-white/20 backdrop-blur-sm disabled:!bg-white/10 disabled:text-gray-800 disabled:border-gray-300 disabled:opacity-100'
          >
            <Settings className='h-4 w-4 mr-2' />
            Settings
          </Button>

          <div className='ml-auto flex items-center gap-4 text-blue-100 text-xs'>
            <div className='flex items-center gap-2'>
              <Zap className='h-3 w-3' />
              <span>Full platform access</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TrialStatusBanner;
